import { Link } from '@inertiajs/react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { Badge } from '@/components/ui/badge'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { InboundEmailSummary, BusinessRef } from '@/types'
import { Mail, ArrowRight } from 'lucide-react'

type Props = {
  business: BusinessRef
  inbound_emails: InboundEmailSummary[]
  pagination: PaginationMeta
}

export default function BackofficeBusinessInboundEmailsIndex({ business, inbound_emails, pagination }: Props) {
  return (
    <BackofficeLayout
      title={`Emails — ${business.name}`}
      breadcrumbs={[
        { label: 'Backoffice', href: '/backoffice/dashboard' },
        { label: 'Businesses', href: '/backoffice/businesses' },
        { label: business.name, href: `/backoffice/businesses/${business.id}` },
        { label: 'Inbound Emails' },
      ]}
    >
      <div className="space-y-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Mail className="w-4 h-4 text-muted-foreground" />
            <h1 className="text-xl font-semibold tracking-tight">Inbound Emails</h1>
          </div>
          <p className="text-sm text-muted-foreground font-mono">{business.email}</p>
        </div>

        <section className="border rounded-lg">
          {inbound_emails.length > 0 ? (
            <div>
              {inbound_emails.map((email, i) => (
                <Link
                  key={email.id}
                  href={`/backoffice/businesses/${business.id}/inbound_emails/${email.id}`}
                  className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-foreground truncate">
                      {email.subject || '(no subject)'}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      From: {email.from_address} · {new Date(email.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-3">
                    {email.published ? (
                      <Badge className="bg-green-600 text-white font-normal text-xs">Published</Badge>
                    ) : (
                      <Badge className="bg-secondary text-secondary-foreground font-normal text-xs">Hidden</Badge>
                    )}
                    <ArrowRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="px-4 py-6 text-center text-sm text-muted-foreground">
              No inbound emails received yet
            </div>
          )}
        </section>

        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  )
}
